import { cn } from "@/lib/utils";

/**
 * One-of-several filter above a list: toggle buttons (`aria-pressed`), each with a tabular
 * count. Selected is the neutral `surface-selected` tint, never the accent.
 */
export function FilterChips<T extends string>(props: {
  /** Names the group for assistive technology ("Show"). */
  label: string;
  options: readonly { value: T; label: string; count?: number }[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: a toolbar of toggles, not a form fieldset.
    <div
      role="group"
      aria-label={props.label}
      className={cn("flex flex-wrap gap-1", props.className)}
    >
      {props.options.map((option) => {
        const pressed = option.value === props.value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={pressed}
            onClick={() => props.onChange(option.value)}
            className={cn(
              "inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium transition-colors duration-(--duration-fast) ease-(--ease-out)",
              "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus",
              pressed
                ? "bg-surface-selected text-fg"
                : "text-fg-secondary hover:bg-surface-hover hover:text-fg",
            )}
          >
            {option.label}
            {option.count !== undefined ? (
              // Muted is under 4.5:1 on the pressed tint: the pressed count steps to secondary.
              <span className={cn("tabular-nums", pressed ? "text-fg-secondary" : "text-fg-muted")}>
                {option.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
